import { useState, useEffect, useMemo } from 'react';
import {
    CheckCircle,
    Clock,
    Flame,
    User,
    Settings,
    Link,
    Mic,
    Shield,
    BarChart3,
    HelpCircle,
    Info,
    ChevronRight,
    LogOut,
    Camera,
    X,
    Bell,
    Zap,
    Globe,
    Sun,
    Moon,
    Monitor,
    Waves,
    TreePine,
    Sunset,
    Brain,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useIntegrations } from '../../contexts/IntegrationsContext';
import { useAppState, AI_PERSONAS, AIPersonaId } from '../../contexts/AppContext';
import { useTheme, ThemeMode } from '../../contexts/ThemeContext';
import { mockUserProfile } from '../../data/mockData';
import {
    getNotificationPermission,
    requestNotificationPermission,
} from '../../services/notificationService';
import {
    ExportableData,
    downloadAsJSON,
    downloadAsCSVZip,
    getExportSummary,
} from '../../services/exportService';
import {
    AIPreferences,
    getAIPreferences,
    saveAIPreferences,
    DEFAULT_AI_PREFERENCES,
} from '../../services/trainingService';
import { verifyAdminAccess } from '../../services/adminService';

interface SettingsItem {
    icon: React.ElementType;
    label: string;
    value?: string;
    onClick?: () => void;
    route?: string;
}

interface SettingsSection {
    title: string;
    items: SettingsItem[];
}

export default function AppProfile() {
    const navigate = useNavigate();
    const { user, logout, updateUser } = useAuth();
    const { integrations } = useIntegrations();
    const { userTimezone, setUserTimezone, aiPersona, setAIPersona, tasks, events, projects, habits, goals } = useAppState();
    const { theme, setTheme } = useTheme();
    const [showThemeModal, setShowThemeModal] = useState(false);
    const [showEditProfile, setShowEditProfile] = useState(false);
    const [showTimezoneModal, setShowTimezoneModal] = useState(false);
    const [showPersonaModal, setShowPersonaModal] = useState(false);
    const [showExportModal, setShowExportModal] = useState(false);
    const [showVoiceModal, setShowVoiceModal] = useState(false);
    const [isExporting, setIsExporting] = useState(false);
    const [voiceRetention, setVoiceRetention] = useState<'7days' | '30days' | 'forever'>('30days');
    const [showTrainingModal, setShowTrainingModal] = useState(false);
    const [aiPrefs, setAiPrefs] = useState<AIPreferences>(getAIPreferences());
    const [editName, setEditName] = useState('');
    const [editEmail, setEditEmail] = useState('');
    const [notificationPermission, setNotificationPermission] = useState<NotificationPermission | 'unsupported'>('default');

    // Check notification permission on mount
    useEffect(() => {
        setNotificationPermission(getNotificationPermission());
    }, []);

    // Calculate real user stats from tasks
    const userStats = useMemo(() => {
        const now = new Date();
        const today = now.toISOString().split('T')[0];

        // Get start of current week (Sunday)
        const startOfWeek = new Date(now);
        startOfWeek.setDate(now.getDate() - now.getDay());
        startOfWeek.setHours(0, 0, 0, 0);
        const startOfWeekStr = startOfWeek.toISOString().split('T')[0];

        // Tasks completed this week
        const tasksThisWeek = tasks.filter(t =>
            t.completed &&
            t.completedAt &&
            t.completedAt >= startOfWeekStr &&
            !t.deletedAt
        ).length;

        // Calculate current streak (consecutive days with at least 1 completed task)
        let streak = 0;
        let checkDate = new Date(now);
        checkDate.setHours(0, 0, 0, 0);

        // If today has no completions yet, start from yesterday
        const todayCompletions = tasks.filter(t =>
            t.completed && t.completedAt?.startsWith(today) && !t.deletedAt
        ).length;

        if (todayCompletions === 0) {
            checkDate.setDate(checkDate.getDate() - 1);
        }

        // Count consecutive days with completions
        for (let i = 0; i < 365; i++) {
            const dateStr = checkDate.toISOString().split('T')[0];
            const completedOnDay = tasks.filter(t =>
                t.completed && t.completedAt?.startsWith(dateStr) && !t.deletedAt
            ).length;

            if (completedOnDay > 0) {
                streak++;
                checkDate.setDate(checkDate.getDate() - 1);
            } else {
                break;
            }
        }

        // Calculate productivity by day of week (for chart)
        const dayTotals = [0, 0, 0, 0, 0, 0, 0]; // Sun-Sat
        const dayCounts = [0, 0, 0, 0, 0, 0, 0];

        // Look at last 4 weeks of data
        const fourWeeksAgo = new Date(now);
        fourWeeksAgo.setDate(fourWeeksAgo.getDate() - 28);
        const fourWeeksAgoStr = fourWeeksAgo.toISOString().split('T')[0];

        tasks.forEach(t => {
            if (t.completed && t.completedAt && t.completedAt >= fourWeeksAgoStr && !t.deletedAt) {
                const completedDate = new Date(t.completedAt);
                const dayOfWeek = completedDate.getDay();
                dayTotals[dayOfWeek]++;
                dayCounts[dayOfWeek] = 1; // Just mark that we have data for this day
            }
        });

        // Convert to percentages (M, T, W, T, F, S, S order)
        const maxTasks = Math.max(...dayTotals, 1);
        const productivityByDay = [1, 2, 3, 4, 5, 6, 0].map(dayIndex =>
            Math.round((dayTotals[dayIndex] / maxTasks) * 100) || 10 // minimum 10% for visibility
        );

        return {
            tasksThisWeek,
            streak,
            productivityByDay,
        };
    }, [tasks]);

    const handleNotificationClick = async () => {
        if (notificationPermission === 'granted') {
            alert('Notifications are enabled! You will receive reminders for tasks and events.');
            return;
        }

        const permission = await requestNotificationPermission();
        setNotificationPermission(permission);

        if (permission === 'granted') {
            alert('✅ Notifications enabled! You will receive reminders for tasks and events.');
        } else if (permission === 'denied') {
            alert('❌ Notifications blocked. Please enable them in your browser settings.');
        }
    };

    // Common timezones
    const timezones = [
        { value: 'America/New_York', label: 'Eastern Time (ET)' },
        { value: 'America/Chicago', label: 'Central Time (CT)' },
        { value: 'America/Denver', label: 'Mountain Time (MT)' },
        { value: 'America/Los_Angeles', label: 'Pacific Time (PT)' },
        { value: 'America/Anchorage', label: 'Alaska Time (AKT)' },
        { value: 'Pacific/Honolulu', label: 'Hawaii Time (HT)' },
        { value: 'Europe/London', label: 'London (GMT/BST)' },
        { value: 'Europe/Paris', label: 'Paris (CET)' },
        { value: 'Europe/Berlin', label: 'Berlin (CET)' },
        { value: 'Asia/Tokyo', label: 'Tokyo (JST)' },
        { value: 'Asia/Shanghai', label: 'Shanghai (CST)' },
        { value: 'Asia/Dubai', label: 'Dubai (GST)' },
        { value: 'Australia/Sydney', label: 'Sydney (AEST)' },
    ];

    const handleLogout = () => {
        logout();
        navigate('/');
    };

    // Use auth user data if available, fallback to mock
    const displayName = user?.name || mockUserProfile.name;
    const displayEmail = user?.email || mockUserProfile.email;
    const displayPlan = user?.plan || mockUserProfile.plan;
    const displayAvatar = user?.avatar || mockUserProfile.avatar;

    const handleEditProfile = () => {
        setEditName(displayName);
        setEditEmail(displayEmail);
        setShowEditProfile(true);
    };

    // Handle photo upload with compression
    const handlePhotoChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        // Validate file type
        if (!file.type.startsWith('image/')) {
            alert('Please select an image file');
            return;
        }

        // Validate file size (max 10MB raw, will be compressed)
        if (file.size > 10 * 1024 * 1024) {
            alert('Image must be less than 10MB');
            return;
        }

        // Compress and resize image using canvas
        const compressImage = (dataUrl: string): Promise<string> => {
            return new Promise((resolve) => {
                const img = new Image();
                img.onload = () => {
                    const canvas = document.createElement('canvas');
                    const ctx = canvas.getContext('2d')!;

                    // Max dimensions for avatar (keeps file size small for localStorage)
                    const maxSize = 200;
                    let { width, height } = img;

                    if (width > height) {
                        if (width > maxSize) {
                            height = (height * maxSize) / width;
                            width = maxSize;
                        }
                    } else {
                        if (height > maxSize) {
                            width = (width * maxSize) / height;
                            height = maxSize;
                        }
                    }

                    canvas.width = width;
                    canvas.height = height;
                    ctx.drawImage(img, 0, 0, width, height);

                    // Compress as JPEG with quality 0.7
                    resolve(canvas.toDataURL('image/jpeg', 0.7));
                };
                img.src = dataUrl;
            });
        };

        // Convert to base64, compress, and update user
        const reader = new FileReader();
        reader.onload = async () => {
            try {
                const rawBase64 = reader.result as string;
                const compressedBase64 = await compressImage(rawBase64);
                updateUser({ avatar: compressedBase64 });
            } catch (error) {
                console.error('Error processing image:', error);
                alert('Error processing image. Please try a smaller image.');
            }
        };
        reader.readAsDataURL(file);
    };

    const handleSaveProfile = () => {
        // In a real app, this would call an API to update the profile
        setShowEditProfile(false);
    };

    const handleSettingsClick = (item: SettingsItem) => {
        if (item.route) {
            navigate(item.route);
        } else if (item.onClick) {
            item.onClick();
        } else if (item.label === 'AI Personality') {
            setShowPersonaModal(true);
        } else if (item.label === 'Export Data') {
            setShowExportModal(true);
        } else if (item.label === 'Voice Data Management') {
            setShowVoiceModal(true);
        } else if (item.label === 'AI Training Mode') {
            setShowTrainingModal(true);
        }
    };

    // Save AI preferences helper
    const handleSaveAIPrefs = (updates: Partial<AIPreferences>) => {
        const updated = { ...aiPrefs, ...updates };
        setAiPrefs(updated);
        saveAIPreferences(updates);
    };

    const handleResetAIPrefs = () => {
        setAiPrefs(DEFAULT_AI_PREFERENCES);
        saveAIPreferences(DEFAULT_AI_PREFERENCES);
    };

    // Get exportable data
    const getExportData = (): ExportableData => ({
        tasks: tasks || [],
        events: events || [],
        projects: projects || [],
        habits: habits || [],
        goals: goals || [],
        contexts: [],
        voiceCaptures: [],
        exportDate: new Date().toISOString(),
        version: '1.0.2',
    });

    const handleExportJSON = () => {
        setIsExporting(true);
        setTimeout(() => {
            downloadAsJSON(getExportData());
            setIsExporting(false);
            setShowExportModal(false);
        }, 500);
    };

    const handleExportCSV = async () => {
        setIsExporting(true);
        await downloadAsCSVZip(getExportData());
        setIsExporting(false);
        setShowExportModal(false);
    };

    const settingsSections: SettingsSection[] = [
        ...(user && (verifyAdminAccess(user.id) || user.email === 'justin@way2will.ai') ? [{
            title: 'Executive Admin',
            items: [
                { icon: Shield, label: 'Wade Admin Portal', route: '/app/admin' },
            ],
        }] : []),
        {
            title: 'Account',
            items: [
                { icon: User, label: 'Personal Information', onClick: handleEditProfile },
                { icon: Bell, label: 'Notifications', value: notificationPermission === 'granted' ? 'On' : notificationPermission === 'denied' ? 'Blocked' : 'Off', onClick: handleNotificationClick },
                { icon: Link, label: 'Subscription & Billing', value: `${displayPlan} Plan`, route: '/app/billing' },
                { icon: Zap, label: 'Connected Apps', route: '/app/connections' },
                { icon: BarChart3, label: 'Analytics', route: '/app/analytics' },
            ],
        },
        {
            title: 'Productivity Tools',
            items: [
                { icon: Zap, label: 'Command Center', route: '/app/command-center' },
                { icon: Zap, label: 'Schedule Optimizer', route: '/app/schedule' },
                { icon: Zap, label: 'Morning Briefing', route: '/app/briefing' },
                { icon: Clock, label: 'Evening Debrief', route: '/app/debrief' },
            ],
        },
        {
            title: 'AI & Voice',
            items: [
                { icon: Brain, label: 'Brain & Identity', route: '/app/brain-settings' },
                { icon: Globe, label: 'Timezone', value: timezones.find(tz => tz.value === userTimezone)?.label || userTimezone, onClick: () => setShowTimezoneModal(true) },
                { icon: Mic, label: 'Voice Settings', route: '/app/voice-settings' },
                { icon: Settings, label: 'AI Personality', value: `${AI_PERSONAS[aiPersona].emoji} ${AI_PERSONAS[aiPersona].name}`, onClick: () => setShowPersonaModal(true) },
                { icon: Settings, label: 'AI Training Mode' },
                { icon: Clock, label: 'Auto-Schedule Preferences', route: '/app/schedule-preferences' },
            ],
        },
        {
            title: 'Data & Privacy',
            items: [
                { icon: Shield, label: 'Privacy Settings', route: '/app/privacy-settings' },
                { icon: Settings, label: 'Export Data' },
                { icon: Mic, label: 'Voice Data Management' },
            ],
        },
        {
            title: 'Support',
            items: [
                { icon: HelpCircle, label: 'Help Center', route: '/app/help' },
                { icon: Settings, label: 'Contact Support', route: '/app/contact' },
                { icon: Settings, label: 'Feature Requests', route: '/app/features' },
                { icon: Info, label: "What's New", route: '/app/changelog' },
            ],
        },
        {
            title: 'About',
            items: [
                { icon: Info, label: 'App Version', value: 'v1.0.2' },
                { icon: Info, label: 'Terms of Service', route: '/app/terms' },
                { icon: Shield, label: 'Privacy Policy', route: '/app/privacy' },
            ],
        },
    ];

    return (
        <div className="pt-4 pb-8 leading-[1.5]">
            {/* Profile Header */}
            <section className="px-4 mb-6">
                <div className="flex flex-col items-center text-center relative">
                    <div className="relative mb-3">
                        {/* Hidden file input */}
                        <input
                            type="file"
                            id="photo-upload"
                            accept="image/*"
                            onChange={handlePhotoChange}
                            className="hidden"
                        />
                        {/* Clickable avatar */}
                        <label htmlFor="photo-upload" className="cursor-pointer block">
                            <div className="w-20 h-20 rounded-full overflow-hidden shadow-[0_4px_16px_rgba(13,95,107,0.08),0_2px_4px_rgba(13,95,107,0.04)]">
                                <img
                                    src={displayAvatar}
                                    alt={displayName}
                                    className="w-full h-full object-cover"
                                />
                            </div>
                            {/* Camera overlay button */}
                            <div className="absolute bottom-0 right-0 w-7 h-7 bg-[#0D5F6B] rounded-full flex items-center justify-center shadow-lg pointer-events-none">
                                <Camera className="w-3.5 h-3.5 text-white" />
                            </div>
                        </label>
                    </div>
                    <h1 className="text-xl font-semibold text-[#1A2832]">
                        {displayName}
                    </h1>
                    <p className="text-sm text-[#4A5C6A] mb-2">{displayEmail}</p>
                    <div className="px-3 py-1 rounded-full bg-[#D9EEF2]">
                        <span className="text-sm font-semibold text-[#2A7A85] capitalize">
                            {displayPlan} Plan
                        </span>
                    </div>
                </div>
            </section>

            {/* Quick Stats Cards */}
            <section className="px-4 mb-6">
                <div className="grid grid-cols-3 gap-3">
                    <StatCard
                        icon={CheckCircle}
                        value={userStats.tasksThisWeek.toString()}
                        label="Completed"
                        sublabel="this week"
                    />
                    <StatCard
                        icon={Flame}
                        value={`${userStats.streak}d`}
                        label="Streak"
                        sublabel="current"
                    />
                    <StatCard
                        icon={Clock}
                        value={events.filter(e => !e.deletedAt && e.date && e.date >= new Date().toISOString().split('T')[0]).length.toString()}
                        label="Events"
                        sublabel="upcoming"
                    />
                </div>
            </section>

            {/* Integrations Section */}
            <section className="px-4 mb-6">
                <h2 className="text-base font-semibold text-[#1A2832] dark:text-dark-text mb-3">Integrations</h2>
                <div className="bg-white dark:bg-dark-card rounded-2xl overflow-hidden shadow-[0_4px_16px_rgba(13,95,107,0.08),0_2px_4px_rgba(13,95,107,0.04)] dark:shadow-dark">
                    {integrations.filter(i => i.connected).slice(0, 5).map((integration, index) => (
                        <div
                            key={integration.id}
                            className={`flex items-center justify-between px-4 py-4 ${index < Math.min(integrations.length, 5) - 1 ? 'border-b border-[#E5EBF0] dark:border-dark-border' : ''
                                }`}
                        >
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#F5F8FA] dark:bg-dark-elevated">
                                    <img
                                        src={`https://api.iconify.design/${integration.service === 'calendar' ? 'logos:google-calendar' :
                                            integration.service === 'gmail' ? 'logos:google-gmail' :
                                                integration.service === 'slack' ? 'logos:slack-icon' :
                                                    integration.service === 'notion' ? 'logos:notion-icon' :
                                                        integration.service === 'linear' ? 'logos:linear-icon' :
                                                            integration.service === 'todoist' ? 'logos:todoist-icon' :
                                                                integration.service === 'asana' ? 'logos:asana-icon' :
                                                                    integration.service === 'trello' ? 'logos:trello' :
                                                                        integration.service === 'dropbox' ? 'logos:dropbox' :
                                                                            'logos:google-drive'
                                            }.svg`}
                                        alt={integration.name}
                                        className="w-5 h-5"
                                    />
                                </div>
                                <div>
                                    <span className="text-base text-[#1A2832] dark:text-white">
                                        {integration.name}
                                    </span>
                                    <p className="text-xs text-[#7B8C9B] dark:text-dark-text-muted">
                                        {integration.lastSync
                                            ? `Last sync: ${integration.lastSync}`
                                            : integration.connected
                                                ? 'Connected'
                                                : 'Not connected'}
                                    </p>
                                </div>
                            </div>
                            <div
                                className={`px-2 py-1 rounded text-xs font-semibold ${integration.connected
                                    ? 'bg-[#D5F2EA] text-[#3DA58A]'
                                    : 'bg-[#F5F8FA] text-[#7B8C9B]'
                                    }`}
                            >
                                {integration.connected ? 'Connected' : 'Not Connected'}
                            </div>
                        </div>
                    ))}
                    <button
                        onClick={() => navigate('/app/connections')}
                        className="w-full px-4 py-4 text-center text-[#0D5F6B] font-semibold border-t border-[#E5EBF0] hover:bg-[#F5F8FA] transition"
                    >
                        + Add Integration
                    </button>
                </div>
            </section>

            {/* Analytics Preview */}
            <section className="px-4 mb-6">
                <div className="flex items-center justify-between mb-3">
                    <h2 className="text-base font-semibold text-[#1A2832] dark:text-dark-text">Analytics</h2>
                    <button className="text-sm font-semibold text-[#0D5F6B]">View All</button>
                </div>
                <div className="bg-white dark:bg-dark-card rounded-2xl p-4 shadow-[0_4px_16px_rgba(13,95,107,0.08),0_2px_4px_rgba(13,95,107,0.04)] dark:shadow-dark">
                    <div className="flex items-center gap-3 mb-4">
                        <BarChart3 className="w-5 h-5 text-[#0D5F6B]" />
                        <span className="text-base font-semibold text-[#1A2832] dark:text-dark-text">
                            Weekly Productivity
                        </span>
                    </div>
                    {/* Simple bar chart visualization */}
                    <div className="flex items-end justify-between h-20 gap-2">
                        {userStats.productivityByDay.map((height, i) => (
                            <div key={i} className="flex-1 flex flex-col items-center gap-1">
                                <div
                                    className="w-full rounded-t bg-[#0D5F6B]"
                                    style={{ height: `${height}%` }}
                                ></div>
                                <span className="text-xs text-[#7B8C9B]">
                                    {['M', 'T', 'W', 'T', 'F', 'S', 'S'][i]}
                                </span>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Settings Sections */}
            {settingsSections.map((section) => (
                <section key={section.title} className="px-4 mb-6">
                    <h2 className="text-base font-semibold text-[#1A2832] dark:text-dark-text mb-3">
                        {section.title}
                    </h2>
                    <div className="bg-white dark:bg-dark-card rounded-2xl overflow-hidden shadow-[0_4px_16px_rgba(13,95,107,0.08),0_2px_4px_rgba(13,95,107,0.04)] dark:shadow-dark">
                        {section.items.map((item, index) => (
                            <button
                                key={item.label}
                                onClick={() => handleSettingsClick(item)}
                                className={`w-full flex items-center justify-between px-4 py-4 active:bg-[#F5F8FA] dark:active:bg-dark-elevated hover:bg-[#F5F8FA] dark:hover:bg-dark-elevated transition ${index < section.items.length - 1 ? 'border-b border-[#E5EBF0] dark:border-dark-border' : ''
                                    }`}
                            >
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#F5F8FA] dark:bg-dark-elevated">
                                        <item.icon className="w-5 h-5 text-[#0D5F6B] dark:text-dark-teal-light" />
                                    </div>
                                    <span className="text-base text-[#1A2832] dark:text-white">{item.label}</span>
                                </div>
                                <div className="flex items-center gap-2">
                                    {item.value && (
                                        <span className="text-sm text-[#4A5C6A] dark:text-dark-text-secondary">{item.value}</span>
                                    )}
                                    <ChevronRight className="w-5 h-5 text-[#7B8C9B]" />
                                </div>
                            </button>
                        ))}
                    </div>
                </section>
            ))}

            {/* Logout Button */}
            <section className="px-4">
                <button
                    onClick={handleLogout}
                    className="w-full flex items-center justify-center gap-2 py-4 rounded-xl border-2 border-[#D66B5C] text-[#D66B5C] active:bg-[#D66B5C]/10"
                >
                    <LogOut className="w-5 h-5" />
                    <span className="text-base font-semibold">Log Out</span>
                </button>
            </section>

            {/* Edit Profile Modal */}
            {showEditProfile && (
                <div className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center">
                    <div className="bg-white dark:bg-dark-card w-full sm:max-w-md sm:rounded-2xl rounded-t-2xl">
                        <div className="p-4 border-b border-[#EEF3F6] flex justify-between items-center">
                            <h2 className="text-lg font-semibold text-[#1A2832]">Edit Profile</h2>
                            <button
                                onClick={() => setShowEditProfile(false)}
                                className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-[#F5F8FA]"
                            >
                                <X className="w-5 h-5 text-[#7B8C9B]" />
                            </button>
                        </div>

                        <div className="p-4 space-y-4">
                            {/* Avatar */}
                            <div className="flex justify-center">
                                <div className="relative">
                                    <div className="w-24 h-24 rounded-full overflow-hidden">
                                        <img
                                            src={displayAvatar}
                                            alt={displayName}
                                            className="w-full h-full object-cover"
                                        />
                                    </div>
                                    <button className="absolute bottom-0 right-0 w-8 h-8 bg-[#0D5F6B] rounded-full flex items-center justify-center">
                                        <Camera className="w-4 h-4 text-white" />
                                    </button>
                                </div>
                            </div>

                            {/* Name */}
                            <div>
                                <label className="block text-sm font-medium text-[#1A2832] mb-2">Name</label>
                                <input
                                    type="text"
                                    value={editName}
                                    onChange={(e) => setEditName(e.target.value)}
                                    className="w-full px-4 py-3 rounded-xl border border-[#DCE4EA] focus:outline-none focus:border-[#0D5F6B] text-[#1A2832]"
                                />
                            </div>

                            {/* Email */}
                            <div>
                                <label className="block text-sm font-medium text-[#1A2832] mb-2">Email</label>
                                <input
                                    type="email"
                                    value={editEmail}
                                    onChange={(e) => setEditEmail(e.target.value)}
                                    className="w-full px-4 py-3 rounded-xl border border-[#DCE4EA] focus:outline-none focus:border-[#0D5F6B] text-[#1A2832]"
                                />
                            </div>

                            {/* Save Button */}
                            <button
                                onClick={handleSaveProfile}
                                className="w-full py-4 rounded-xl bg-[#0D5F6B] text-white text-base font-semibold"
                            >
                                Save Changes
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Timezone Modal */}
            {showTimezoneModal && (
                <div className="fixed inset-0 bg-black/50 z-50 flex items-end">
                    <div className="bg-white dark:bg-dark-card w-full rounded-t-[28px] p-6 pb-8 max-h-[70vh] overflow-hidden flex flex-col">
                        <div className="flex justify-between items-center mb-4">
                            <h2 className="text-xl font-semibold text-[#1A2832]">Select Timezone</h2>
                            <button
                                onClick={() => setShowTimezoneModal(false)}
                                className="w-8 h-8 flex items-center justify-center"
                            >
                                <X className="w-6 h-6 text-[#7B8C9B]" />
                            </button>
                        </div>
                        <p className="text-sm text-[#7B8C9B] mb-4">
                            This timezone will be used by the AI assistant and throughout the app.
                        </p>
                        <div className="overflow-y-auto flex-1 space-y-2">
                            {timezones.map((tz) => (
                                <button
                                    key={tz.value}
                                    onClick={() => {
                                        setUserTimezone(tz.value);
                                        setShowTimezoneModal(false);
                                    }}
                                    className={`w-full px-4 py-3 rounded-xl text-left flex items-center justify-between transition-colors ${userTimezone === tz.value
                                        ? 'bg-[#D9EEF2] border-2 border-[#0D5F6B]'
                                        : 'bg-gray-50 hover:bg-gray-100'
                                        }`}
                                >
                                    <span className={`font-medium ${userTimezone === tz.value ? 'text-[#0D5F6B]' : 'text-[#1A2832]'}`}>
                                        {tz.label}
                                    </span>
                                    {userTimezone === tz.value && (
                                        <CheckCircle className="w-5 h-5 text-[#0D5F6B]" />
                                    )}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            {/* Theme Selection Modal */}
            {showThemeModal && (
                <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
                    <div className="bg-white dark:bg-dark-card rounded-2xl p-6 w-full max-w-sm">
                        <div className="flex items-center justify-between mb-4">
                            <h2 className="text-xl font-semibold text-[#1A2832]">Theme</h2>
                            <button
                                onClick={() => setShowThemeModal(false)}
                                className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                            >
                                <X className="w-5 h-5 text-[#7B8C9B]" />
                            </button>
                        </div>
                        <p className="text-sm text-[#7B8C9B] mb-4">
                            Choose your preferred appearance.
                        </p>
                        <div className="space-y-2 max-h-[60vh] overflow-y-auto">
                            {([
                                { value: 'light' as ThemeMode, label: 'Light', icon: Sun, desc: 'Clean and bright', color: '#F8FAFB', accent: '#0D5F6B' },
                                { value: 'dark' as ThemeMode, label: 'Dark', icon: Moon, desc: 'Easy on the eyes', color: '#0F1419', accent: '#5BC4A8' },
                                { value: 'system' as ThemeMode, label: 'System', icon: Monitor, desc: 'Match your device', color: 'linear-gradient(135deg, #F8FAFB 50%, #0F1419 50%)', accent: '#7B8C9B' },
                                { value: 'ocean' as ThemeMode, label: 'Ocean', icon: Waves, desc: 'Deep blue vibes', color: '#0A1628', accent: '#29B6F6' },
                                { value: 'forest' as ThemeMode, label: 'Forest', icon: TreePine, desc: 'Natural greens', color: '#0F1A14', accent: '#66BB6A' },
                                { value: 'sunset' as ThemeMode, label: 'Sunset', icon: Sunset, desc: 'Warm and cozy', color: '#1A1218', accent: '#FF7043' },
                            ]).map((option) => {
                                const Icon = option.icon;
                                const isSelected = theme === option.value;
                                return (
                                    <button
                                        key={option.value}
                                        onClick={() => {
                                            setTheme(option.value);
                                            setShowThemeModal(false);
                                        }}
                                        className={`w-full p-3 rounded-xl text-left flex items-center gap-3 transition-all ${isSelected
                                            ? 'ring-2 ring-offset-2 ring-[#0D5F6B] dark:ring-dark-teal-light'
                                            : 'hover:bg-gray-50 dark:hover:bg-dark-elevated'
                                            }`}
                                    >
                                        {/* Color Preview Circle */}
                                        <div
                                            className="w-10 h-10 rounded-full flex items-center justify-center border-2 border-white shadow-md"
                                            style={{ background: option.color }}
                                        >
                                            <Icon className="w-5 h-5" style={{ color: option.accent }} />
                                        </div>
                                        <div className="flex-1">
                                            <span className={`font-medium ${isSelected ? 'text-[#0D5F6B] dark:text-dark-teal-light' : 'text-[#1A2832] dark:text-white'}`}>
                                                {option.label}
                                            </span>
                                            <p className="text-xs text-[#7B8C9B] dark:text-dark-text-muted">{option.desc}</p>
                                        </div>
                                        {isSelected && (
                                            <CheckCircle className="w-5 h-5 text-[#0D5F6B] dark:text-dark-teal-light" />
                                        )}
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                </div>
            )}

            {/* AI Persona Selection Modal */}
            {showPersonaModal && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white dark:bg-dark-card rounded-2xl p-6 w-full max-w-md max-h-[80vh] flex flex-col">
                        <div className="flex items-center justify-between mb-4">
                            <h2 className="text-xl font-semibold text-[#1A2832]">AI Personality</h2>
                            <button
                                onClick={() => setShowPersonaModal(false)}
                                className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                            >
                                <X className="w-5 h-5 text-[#7B8C9B]" />
                            </button>
                        </div>
                        <p className="text-sm text-[#7B8C9B] mb-4">
                            Choose how your AI assistant communicates with you.
                        </p>
                        <div className="overflow-y-auto flex-1 space-y-3">
                            {(Object.keys(AI_PERSONAS) as AIPersonaId[]).map((personaId) => {
                                const persona = AI_PERSONAS[personaId];
                                const isSelected = aiPersona === personaId;
                                return (
                                    <button
                                        key={personaId}
                                        onClick={() => {
                                            setAIPersona(personaId);
                                            setShowPersonaModal(false);
                                        }}
                                        className={`w-full p-4 rounded-xl text-left transition-all ${isSelected
                                            ? 'bg-[#D9EEF2] border-2 border-[#0D5F6B] shadow-md'
                                            : 'bg-gray-50 hover:bg-gray-100 border-2 border-transparent'
                                            }`}
                                    >
                                        <div className="flex items-start gap-3">
                                            <span className="text-2xl">{persona.emoji}</span>
                                            <div className="flex-1">
                                                <div className="flex items-center justify-between">
                                                    <span className={`font-semibold ${isSelected ? 'text-[#0D5F6B]' : 'text-[#1A2832]'}`}>
                                                        {persona.name}
                                                    </span>
                                                    {isSelected && (
                                                        <CheckCircle className="w-5 h-5 text-[#0D5F6B]" />
                                                    )}
                                                </div>
                                                <p className="text-sm text-[#7B8C9B] mt-1">
                                                    {persona.description}
                                                </p>
                                            </div>
                                        </div>
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                </div>
            )}

            {/* Export Data Modal */}
            {showExportModal && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white dark:bg-dark-card rounded-2xl p-6 w-full max-w-md">
                        <div className="flex items-center justify-between mb-4">
                            <h2 className="text-xl font-semibold text-[#1A2832]">Export Your Data</h2>
                            <button
                                onClick={() => setShowExportModal(false)}
                                className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                            >
                                <X className="w-5 h-5 text-[#7B8C9B]" />
                            </button>
                        </div>

                        <p className="text-sm text-[#7B8C9B] mb-4">
                            Download all your data including tasks, events, projects, habits, and goals.
                        </p>

                        {/* Export Summary */}
                        <div className="bg-[#F5F8FA] rounded-xl p-4 mb-6">
                            <p className="text-sm font-medium text-[#1A2832] mb-2">Data Summary</p>
                            {(() => {
                                const summary = getExportSummary(getExportData());
                                return (
                                    <div className="space-y-1">
                                        {summary.breakdown.map((item) => (
                                            <div key={item.type} className="flex justify-between text-sm">
                                                <span className="text-[#7B8C9B]">{item.type}</span>
                                                <span className="text-[#1A2832] font-medium">{item.count}</span>
                                            </div>
                                        ))}
                                        <div className="border-t border-[#DCE4EA] pt-1 mt-2 flex justify-between text-sm">
                                            <span className="text-[#1A2832] font-semibold">Total Items</span>
                                            <span className="text-[#0D5F6B] font-bold">{summary.totalItems}</span>
                                        </div>
                                    </div>
                                );
                            })()}
                        </div>

                        {/* Export Options */}
                        <div className="space-y-3">
                            <button
                                onClick={handleExportJSON}
                                disabled={isExporting}
                                className="w-full py-4 rounded-xl bg-[#0D5F6B] text-white font-semibold flex items-center justify-center gap-2 hover:bg-[#0a4d57] transition disabled:opacity-50"
                            >
                                {isExporting ? (
                                    <>
                                        <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                                        Exporting...
                                    </>
                                ) : (
                                    <>📄 Export as JSON</>
                                )}
                            </button>
                            <button
                                onClick={handleExportCSV}
                                disabled={isExporting}
                                className="w-full py-4 rounded-xl border-2 border-[#0D5F6B] text-[#0D5F6B] font-semibold flex items-center justify-center gap-2 hover:bg-[#F5F8FA] transition disabled:opacity-50"
                            >
                                {isExporting ? (
                                    <>
                                        <div className="w-5 h-5 border-2 border-[#0D5F6B]/30 border-t-[#0D5F6B] rounded-full animate-spin" />
                                        Exporting...
                                    </>
                                ) : (
                                    <>📊 Export as CSV (ZIP)</>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Voice Data Management Modal */}
            {showVoiceModal && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white dark:bg-dark-card rounded-2xl p-6 w-full max-w-md">
                        <div className="flex items-center justify-between mb-4">
                            <h2 className="text-xl font-semibold text-[#1A2832]">Voice Data</h2>
                            <button
                                onClick={() => setShowVoiceModal(false)}
                                className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                            >
                                <X className="w-5 h-5 text-[#7B8C9B]" />
                            </button>
                        </div>

                        <p className="text-sm text-[#7B8C9B] mb-6">
                            Manage how your voice captures are stored and retained.
                        </p>

                        {/* Voice Retention Settings */}
                        <div className="mb-6">
                            <p className="text-sm font-medium text-[#1A2832] mb-3">Data Retention Period</p>
                            <div className="space-y-2">
                                {(['7days', '30days', 'forever'] as const).map((option) => {
                                    const labels = {
                                        '7days': '7 Days',
                                        '30days': '30 Days',
                                        'forever': 'Forever',
                                    };
                                    const isSelected = voiceRetention === option;
                                    return (
                                        <button
                                            key={option}
                                            onClick={() => setVoiceRetention(option)}
                                            className={`w-full px-4 py-3 rounded-xl text-left flex items-center justify-between transition-colors ${isSelected
                                                ? 'bg-[#D9EEF2] border-2 border-[#0D5F6B]'
                                                : 'bg-gray-50 hover:bg-gray-100 border-2 border-transparent'
                                                }`}
                                        >
                                            <span className={`font-medium ${isSelected ? 'text-[#0D5F6B]' : 'text-[#1A2832]'}`}>
                                                {labels[option]}
                                            </span>
                                            {isSelected && <CheckCircle className="w-5 h-5 text-[#0D5F6B]" />}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>

                        {/* Voice Captures Info */}
                        <div className="bg-[#F5F8FA] rounded-xl p-4 mb-6">
                            <div className="flex items-center gap-3 mb-2">
                                <Mic className="w-5 h-5 text-[#0D5F6B]" />
                                <span className="font-medium text-[#1A2832]">Voice Captures</span>
                            </div>
                            <p className="text-sm text-[#7B8C9B]">
                                No voice captures stored. Voice data is processed in real-time and not retained by default.
                            </p>
                        </div>

                        {/* Delete All Button */}
                        <button
                            onClick={() => {
                                if (confirm('Are you sure you want to delete all voice data? This cannot be undone.')) {
                                    // In a real app, this would delete all voice data
                                    alert('All voice data has been deleted.');
                                    setShowVoiceModal(false);
                                }
                            }}
                            className="w-full py-4 rounded-xl border-2 border-[#D66B5C] text-[#D66B5C] font-semibold hover:bg-[#FFEAE7] transition"
                        >
                            🗑️ Delete All Voice Data
                        </button>
                    </div>
                </div>
            )}

            {/* AI Training Mode Modal */}
            {showTrainingModal && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white dark:bg-dark-card rounded-2xl p-6 w-full max-w-md max-h-[85vh] overflow-y-auto">
                        <div className="flex items-center justify-between mb-4">
                            <h2 className="text-xl font-semibold text-[#1A2832]">AI Training Mode</h2>
                            <button
                                onClick={() => setShowTrainingModal(false)}
                                className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                            >
                                <X className="w-5 h-5 text-[#7B8C9B]" />
                            </button>
                        </div>

                        <p className="text-sm text-[#7B8C9B] mb-6">
                            Teach the AI your preferences for better personalization.
                        </p>

                        {/* Scheduling Aggressiveness */}
                        <div className="mb-6">
                            <p className="text-sm font-medium text-[#1A2832] mb-2">Scheduling Aggressiveness</p>
                            <p className="text-xs text-[#7B8C9B] mb-3">How packed should your schedule be?</p>
                            <div className="flex items-center gap-3">
                                <span className="text-xs text-[#7B8C9B]">Conservative</span>
                                <input
                                    type="range"
                                    min="1"
                                    max="10"
                                    value={aiPrefs.schedulingAggressiveness}
                                    onChange={(e) => handleSaveAIPrefs({ schedulingAggressiveness: parseInt(e.target.value) })}
                                    className="flex-1 h-2 rounded-full appearance-none bg-[#DCE4EA] [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-[#0D5F6B]"
                                />
                                <span className="text-xs text-[#7B8C9B]">Aggressive</span>
                            </div>
                            <p className="text-center text-sm font-semibold text-[#0D5F6B] mt-2">{aiPrefs.schedulingAggressiveness}/10</p>
                        </div>

                        {/* Buffer Time */}
                        <div className="mb-6">
                            <p className="text-sm font-medium text-[#1A2832] mb-2">Buffer Between Meetings</p>
                            <div className="grid grid-cols-4 gap-2">
                                {[5, 10, 15, 30].map((mins) => (
                                    <button
                                        key={mins}
                                        onClick={() => handleSaveAIPrefs({ bufferTimeBetweenMeetings: mins })}
                                        className={`py-2 rounded-xl text-sm font-medium transition ${aiPrefs.bufferTimeBetweenMeetings === mins
                                            ? 'bg-[#0D5F6B] text-white'
                                            : 'bg-[#F5F8FA] text-[#1A2832] hover:bg-[#DCE4EA]'
                                            }`}
                                    >
                                        {mins} min
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Work Hours */}
                        <div className="mb-6">
                            <p className="text-sm font-medium text-[#1A2832] mb-2">Work Hours</p>
                            <div className="flex items-center gap-2">
                                <input
                                    type="time"
                                    value={aiPrefs.workStartTime}
                                    onChange={(e) => handleSaveAIPrefs({ workStartTime: e.target.value })}
                                    className="flex-1 px-3 py-2 rounded-xl border border-[#DCE4EA] text-sm"
                                />
                                <span className="text-[#7B8C9B]">to</span>
                                <input
                                    type="time"
                                    value={aiPrefs.workEndTime}
                                    onChange={(e) => handleSaveAIPrefs({ workEndTime: e.target.value })}
                                    className="flex-1 px-3 py-2 rounded-xl border border-[#DCE4EA] text-sm"
                                />
                            </div>
                        </div>

                        {/* Briefing Verbosity */}
                        <div className="mb-6">
                            <p className="text-sm font-medium text-[#1A2832] mb-2">Briefing Detail Level</p>
                            <div className="grid grid-cols-3 gap-2">
                                {(['concise', 'detailed', 'comprehensive'] as const).map((level) => (
                                    <button
                                        key={level}
                                        onClick={() => handleSaveAIPrefs({ briefingVerbosity: level })}
                                        className={`py-2 rounded-xl text-sm font-medium capitalize transition ${aiPrefs.briefingVerbosity === level
                                            ? 'bg-[#0D5F6B] text-white'
                                            : 'bg-[#F5F8FA] text-[#1A2832] hover:bg-[#DCE4EA]'
                                            }`}
                                    >
                                        {level}
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Reset Button */}
                        <button
                            onClick={() => {
                                if (confirm('Reset all AI preferences to defaults?')) {
                                    handleResetAIPrefs();
                                }
                            }}
                            className="w-full py-3 rounded-xl border-2 border-[#7B8C9B] text-[#7B8C9B] font-semibold hover:bg-[#F5F8FA] transition"
                        >
                            Reset to Defaults
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}

// Stat Card Component
interface StatCardProps {
    icon: React.ElementType;
    value: string;
    label: string;
    sublabel: string;
}

function StatCard({ icon: Icon, value, label, sublabel }: StatCardProps) {
    return (
        <div className="bg-white dark:bg-dark-card rounded-xl p-3 shadow-[0_4px_16px_rgba(13,95,107,0.08),0_2px_4px_rgba(13,95,107,0.04)] dark:shadow-dark text-center">
            <div className="w-8 h-8 mx-auto mb-2 flex items-center justify-center rounded-full bg-[#D9EEF2] dark:bg-dark-elevated">
                <Icon className="w-4 h-4 text-[#0D5F6B] dark:text-dark-teal-light" />
            </div>
            <div className="text-lg font-semibold text-[#1A2832] dark:text-dark-text">{value}</div>
            <div className="text-xs text-[#7B8C9B] dark:text-dark-text-muted">
                {label}
                <br />
                {sublabel}
            </div>
        </div>
    );
}
